import { ContentBlocks } from "src/ui/ContentBlocks";
import { SeoHead } from "src/ui/SeoHead";

export default function HomePage({ prefix, title, canonical, data }) {
  return (
    <>
      <SeoHead title={title} canonical={canonical} />
      <ContentBlocks data={data} prefix={prefix} />
    </>
  );
}

export async function getStaticProps() {
  return {
    props: {
      prefix: "competency-based-interviews",
      title: "Competency-Based Interviews",
      canonical: "/candidates/competency-based-interviews/",
      data: [
        {
          component: "Hero",
          props: {
            image: "/assets/candidate_competency_based_interviews_hero.webp",
            tagline: "Candidates",
            title: "Competency-Based Interviews",
            buttons: [
              { label: "Apply for a Job now", url: "/jobs/", variant: "navy" },
              { label: "Upload Your CV", url: "/upload-cv/", variant: "light" },
            ],
          },
        },
        {
          component: "Content",
          props: {
            title: "What to Expect in a Competency-Based Interview",
            description:
              "<p>A competency-based interview is a structured way for employers to assess how well you are likely to perform in a role. Unlike traditional interviews, these focus on real-life examples of when you've demonstrated key skills and behaviors.</p><p>The key principle behind this method is that past behavior is the best predictor of future success. You will be asked to provide specific examples that showcase your skills, rather than discussing general approaches or hypothetical scenarios.</p>",
          },
        },
        {
          component: "ContentSideImage",
          props: {
            title: "How Competency-Based Interviews Work",
            description: `<ul><li>The interviewer will ask questions like "Describe a time when…" or "Tell me about a situation where you…"</li><li>You’ll need to give real examples from your past experiences to demonstrate your abilities.</li><li>All candidates for the role will be asked the same set of questions, ensuring fairness in the hiring process.</li><li>Interviewers will take detailed notes to accurately assess your responses.</li></ul><p>💡 <strong>Top Tip</strong>: Try to prepare multiple examples rather than relying on the same one for different questions—this shows greater versatility and breadth of experience.</p>`,
            image: "/assets/candidate_competency_based_interviews_content.webp",
          },
        },
        {
          component: "Accordions",
          props: {
            className: "",
            title: "How to Prepare for a Competency-Based Interview",
            items: [
              {
                title: "1. Identify Key Competencies",
                content:
                  "<p>Think about the skills and behaviors likely to be important for the job. These may include:</p><ul><li><strong>Leadership</strong></li><li><strong>Problem-Solving</strong></li><li><strong>Communication & Influence</strong></li><li><strong>Teamwork & Collaboration</strong></li><li><strong>Adaptability & Resilience</strong></li><li><strong>Attention to Detail</strong></li><li><strong>Decision-Making</strong></li><li><strong>Time Management & Prioritization</strong></li></ul><p>💡 <strong>Tip:</strong> Review the job description and highlight the competencies mentioned.</p>",
              },
              {
                title: "2. Gather Your Examples",
                content:
                  "<p>Reflect on your past experiences where you’ve successfully demonstrated these competencies. Examples can come from:</p><ul><li><strong>Work experience</strong></li><li><strong>Education & academic projects</strong></li><li><strong>Volunteering or personal projects</strong></li><li><strong>Sports, hobbies, or extracurricular activities</strong></li></ul><p>💡 <strong>Tip:</strong> Avoid generic responses—focus on <strong>specific</strong> situations where <strong>you personally made an impact.</strong></p>",
              },
              {
                title: "3. Use the STAR Method",
                content:
                  "<p>A simple structure to craft compelling answers:</p><ul><li><strong>S</strong>ituation – Set the scene (briefly describe the context)</li><li><strong>T</strong>ask – What was your role or responsibility?</li><li><strong>A</strong>ction – What steps did you take? (Focus on <strong>your</strong> actions, not the team's.)</li><li><strong>R</strong>esult – What was the outcome? (Quantify success where possible.)</li></ul><p>💡 Example Response Using STAR:<br/><strong>Question:</strong> Describe a time when you had to resolve a difficult conflict in the workplace.</p>" +
                  "<p>✅ <strong>Situation:</strong> A customer was unhappy about a delayed product shipment and was considering canceling their contract.<br/>✅ <strong>Task:</strong> As the Account Manager, I was responsible for addressing the issue and retaining the customer.<br/>✅ <strong>Action:</strong> I immediately contacted the customer to acknowledge their frustration, explained the delay, and offered a temporary solution. I also worked internally with the logistics team to fast-track their next shipment.<br/>✅ <strong>Result:</strong> The customer appreciated our transparency and stayed with us, renewing their contract for another year.</p>",
              },
              {
                title: "During the Interview: Best Practices",
                content:
                  "<p>✔ <strong>Listen carefully</strong> – Make sure you answer exactly what’s being asked.<br/>✔ <strong>Be specific</strong> – Provide a clear example rather than speaking in general terms.<br/>✔ <strong>Focus on your role</strong> – Use “I” statements instead of “we” to highlight your contribution.<br/>✔ <strong>Stay concise</strong> – Keep answers structured and to the point.<br/>✔ <strong>Remain professional and confident</strong> – Even if you feel nervous, take a deep breath and speak clearly.</p><p>💡 <strong>Bonus Tip:</strong> If you need a moment to think, <strong>pause briefly</strong> instead of rushing your response. It’s perfectly fine to take a second to collect your thoughts.</p>",
              },
            ],
          },
        },
        {
          component: "Content",
          props: {
            className: "py-5",
            title: "Final Thoughts",
            description:
              "<p>A well-prepared competency-based interview can be your chance to shine. By using the STAR method, preparing multiple examples, and keeping responses clear and structured, you’ll be able to confidently demonstrate your skills to potential employers.</p>",
          },
        },
        {
          component: "ContentSideImages",
          props: {
            className: "pt-6 pb-7 mb-5 py-md-7",
            title: "Start applying for your ideal job today!",
            reverse: true,
            image_1: "/assets/featured_clients_cta_1.webp",
            image_2: "/assets/featured_clients_cta_2.webp",
            image_2_hexagon: false,
            buttons: [
              {
                label: "Upload your CV",
                url: "/upload-cv/",
              },
            ],
          },
        },
      ],
    },
  };
}
